import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import React, { useState } from 'react';
import {
    useGetAllCategoriesQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
    useGetAllProjectsQuery
} from '../store/slices/apiSlice';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Table } from '../components/ui/Table';
import {
    Plus,
    Edit,
    Trash2,
    Check,
    X,
    Tag,
    FolderOpen,
    Search,
    Loader2,
    Eye,
    RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';

const Category: React.FC = () => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const { data: categoriesResponse, isLoading, error } = useGetAllCategoriesQuery();
    const { data: projectsResponse } = useGetAllProjectsQuery({ limit: 100 });
    const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
    const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
    const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();

    const categories = categoriesResponse?.data || [];

    // Helper to calculate projects count under a specific category
    const getProjectCount = (categoryId: number) => {
        return (projectsResponse?.data || []).filter(
            (proj: any) => proj.category?.category_id === categoryId
        ).length;
    };

    // Filter categories based on search term
    const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Clear form errors when modal is closed
    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
        setFormData({
            name: '',
            description: '',
        });
        setFormErrors({});
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedCategory(null);
        setFormData({
            name: '',
            description: '',
        });
        setFormErrors({});
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.name.trim()) {
            errors.name = 'Category name is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            await createCategory({ 
                name: formData.name.trim(),
                description: formData.description.trim() || null
            });
            toast.success('Category created successfully!');
            handleCloseCreateModal();
        } catch (error) {
            toast.error('Failed to create category');
        }
    };

    const handleUpdateCategory = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedCategory || !validateForm()) return;

        try {
            await updateCategory({
                category_id: selectedCategory.category_id,
                name: formData.name.trim(),
                description: formData.description.trim() || null
            });
            toast.success('Category updated successfully!');
            handleCloseEditModal();
        } catch (error) {
            toast.error('Failed to update category');
        }
    };

    const handleDeleteCategory = async () => {
        if (!selectedCategory) return;

        try {
            await deleteCategory({ category_id: selectedCategory.category_id });
            toast.success('Category deleted successfully!');
            setIsDeleteModalOpen(false);
            setSelectedCategory(null);
        } catch (error) {
            toast.error('Failed to delete category');
        }
    };

    const openEditModal = (category: any) => {
        setSelectedCategory(category);
        setFormData({
            name: category.name,
            description: category.description || '',
        });
        setFormErrors({});
        setIsEditModalOpen(true);
    };

    const openDeleteModal = (category: any) => {
        setSelectedCategory(category);
        setIsDeleteModalOpen(true);
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-600 text-sm">Error loading categories</div>
            </div>
        );
    }

    return (
        <div className="space-y-4 max-w-7xl mx-auto">
            {/* Premium Filter Hub */}
            <Card className="border-none shadow-xl bg-white/80 backdrop-blur-md sticky top-0 z-10">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-1">
                    {/* Search Bar */}
                    <div className="relative flex-1 group">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        <Input
                            placeholder="Search categories by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-12 h-14 bg-gray-50/50 border-gray-200 focus:bg-white transition-all shadow-inner rounded-2xl text-base"
                        />
                    </div>

                    {/* Filter Actions */}
                    <div className="flex items-center gap-4">
                        {searchTerm && (
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setSearchTerm('')}
                                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 h-12 px-6 font-bold text-xs uppercase tracking-widest transition-all shadow-sm"
                            >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Clear
                            </Button>
                        )}
                        <Button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="h-12 px-8 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 font-bold"
                        >
                            <Plus className="w-4 h-4 mr-2" /> Add Category
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Results Header */}
            <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-3">
                    <h3 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em]">Project Taxonomy</h3>
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100 font-bold px-3 py-1 rounded-full">
                        {filteredCategories.length} Categories
                    </Badge>
                </div>
            </div>

            {/* Categories Table */}
            <Card className="p-4">
                {filteredCategories.length === 0 ? (
                    <div className="text-center py-8">
                        <div className="text-gray-400 mb-4">
                            <Tag className="w-12 h-12 mx-auto" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {searchTerm ? 'No categories found' : 'No categories yet'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {searchTerm
                                ? 'Try adjusting your search terms.'
                                : 'Create your first category to get started.'
                            }
                        </p>
                        {!searchTerm && (
                            <Button
                                onClick={() => setIsCreateModalOpen(true)}
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                <Plus className="w-4 h-4 mr-2" /> Add Category
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table
                            columns={[
                                {
                                    key: 'sno',
                                    label: 'S.No',
                                    render: (_: any, __: any, index: number) => (
                                        <div className="font-bold text-gray-400 text-xs">#{index + 1}</div>
                                    ),
                                },
                                {
                                    key: 'name',
                                    label: 'Category Name',
                                    render: (value) => (
                                        <div className="font-medium text-gray-900 text-sm">{value}</div>
                                    ),
                                },
                                {
                                    key: 'description',
                                    label: 'Description',
                                    render: (_, row: any) => (
                                        <div className="text-xs text-gray-500 max-w-xs truncate" title={row.description}>
                                            {row.description || 'No description provided'}
                                        </div>
                                    ),
                                },
                                {
                                    key: 'project_count',
                                    label: 'Total Projects',
                                    render: (_, row: any) => {
                                        const count = getProjectCount(row.category_id);
                                        return (
                                            <Badge
                                                variant="secondary"
                                                className={`font-bold px-3 py-1 rounded-full text-xs ${
                                                    count > 0
                                                        ? 'bg-blue-50 text-blue-700 border-blue-100'
                                                        : 'bg-gray-100 text-gray-500 border-gray-200'
                                                }`}
                                            >
                                                {count} {count === 1 ? 'Project' : 'Projects'}
                                            </Badge>
                                        );
                                    }
                                },
                                {
                                    key: 'created_at',
                                    label: 'Created',
                                    render: (value) => (
                                        <div className="text-xs text-gray-500">
                                            {formatDate(value)}
                                        </div>
                                    ),
                                },
                                {
                                    key: 'actions',
                                    label: 'Actions',
                                    render: (_, row) => (
                                        <div className="flex space-x-1">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openEditModal(row)}
                                                className="text-xs px-2 py-1"
                                            >
                                                <Edit className="w-3 h-3" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => openDeleteModal(row)}
                                                className="text-xs px-2 py-1"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ),
                                },
                            ]}
                            data={filteredCategories}
                        />
                    </div>
                )}
            </Card>

            {/* Create Modal */}
            <Modal
                isOpen={isCreateModalOpen}
                onClose={handleCloseCreateModal}
                title="Add Category"
                size="lg"
            >
                <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Category Name *
                            </label>
                            <Input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Enter category name"
                                className={formErrors.name ? 'border-red-500' : ''}
                            />
                            {formErrors.name && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Enter category description"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                            />
                        </div>
                    </div>

                    {/* Sticky bottom buttons */}
                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCloseCreateModal}
                            className="w-full sm:w-auto text-sm px-4 py-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isCreating}
                            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                        >
                            {isCreating ? 'Creating...' : 'Save Category'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                title="Edit Category"
                size="lg"
            >
                <form onSubmit={handleUpdateCategory} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Category Name *
                            </label>
                            <Input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Enter category name"
                                className={formErrors.name ? 'border-red-500' : ''}
                            />
                            {formErrors.name && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Enter category description"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                            />
                        </div>
                    </div>

                    {/* Sticky bottom buttons */}
                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCloseEditModal}
                            className="w-full sm:w-auto text-sm px-4 py-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isUpdating}
                            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                        >
                            {isUpdating ? 'Updating...' : 'Update Category'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title="Delete Category"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                        Are you sure you want to delete the category{' '}
                        <span className="font-semibold">{selectedCategory?.name}</span>?
                        This action cannot be undone.
                    </p>

                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            variant="outline"
                            onClick={() => setIsDeleteModalOpen(false)}
                            className="w-full sm:w-auto text-sm px-4 py-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteCategory}
                            disabled={isDeleting}
                            className="w-full sm:w-auto text-sm px-4 py-2"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Category'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Category;